import { useEffect, useState } from "react"
import Api from "../api/api"
import { NotificationPreferences } from "../api/models"
import Spinner from "./widgets/Spinner"

const CATEGORIES: { key: keyof NotificationPreferences, label: string, description: string }[] = [
  { key: "productUpdates", label: "Product news", description: "New versions and changes to your plan's products" },
]

// The product-news opt-in (GET/PATCH /me/notification-preferences, backed by
// the custom:product_news Cognito attribute; the sign-up form sets it
// initially). The toggle flips optimistically and rolls back on error; the
// server response is authoritative. Transactional emails (receipts, billing,
// security, refunds, account status) have no switch.
export default function NotificationPreferencesCard({ accessToken }: { accessToken: string }) {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)
  const [error, setError] = useState("")

  useEffect(() => {
    // empty on a hard reload of the page before auth resolves
    if (!accessToken) return
    let cancelled = false
    Api.getNotificationPreferences({ accessToken })
      .then((p) => { if (!cancelled) setPrefs(p) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : "unknown error") })
    return () => { cancelled = true }
  }, [accessToken])

  const toggle = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!prefs) return
    const previous = prefs
    setPrefs({ ...prefs, [key]: value })
    setError("")
    try {
      const saved = await Api.patchNotificationPreferences({ updates: { ...prefs, [key]: value }, accessToken })
      setPrefs(saved)
    } catch (err) {
      setPrefs(previous)
      setError(err instanceof Error ? err.message : "unknown error")
    }
  }

  return (
    <div className="card" style={{ maxWidth: 480, marginTop: 16 }}>
      <div className="detail-grid">
        <div className="detail-item" style={{ gridColumn: "1 / -1" }}>
          <span className="detail-label">Email Notifications</span>
        </div>
      </div>

      {!prefs && !error && <Spinner size={16} />}

      {prefs && CATEGORIES.map(({ key, label, description }) => (
        <label
          key={key}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            className="retro-checkbox"
            checked={prefs[key]}
            onChange={(e) => toggle(key, e.target.checked)}
          />
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span className="detail-value">{label}</span>
            <span className="generate-codes-hint">{description}</span>
          </span>
        </label>
      ))}

      {error && <p className="generate-codes-error">{error}</p>}

      <p className="generate-codes-hint" style={{ marginTop: 12, marginBottom: 0 }}>
        Emails about your purchases, subscription, licenses and account are always sent.
      </p>
    </div>
  )
}
